// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from '@emotion/styled';
import { useIntl, defineMessages } from 'react-intl-next';
import React, { type ReactElement, useCallback, useState } from 'react';

import TableTree, { Cell, Row, Rows } from '@atlaskit/table-tree';
import { type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/new';
import Tooltip from '@atlaskit/tooltip';
import Lozenge from '@atlaskit/lozenge';
import { token } from '@atlaskit/tokens';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';
import OriginTracing from '@atlassiansox/origin-tracing';

import { useDraftsToggle } from '../../controllers/drafts-toggle';
import { getPageTreeItemProperties } from '../../context/page-tree/confluence-table-tree-data';
import { useConfluencePageTreeContext } from '../../context/page-tree/confluence-page-tree-context';
import { Contributors } from '../../components/Contributors';
import { ErrorState } from '../../components/ErrorState';
import ErrorRow from '../../components/ErrorRow';
import { type PageTreeItem } from '../../types';

import LastUpdated from '../last-updated';
import Link from '../link-with-analytics';

import { ButtonWrapper, HoverWrapper, TableTreeWrapper } from './styled';
import type { PageTreeBaseProps, PageTreeItemProperties } from './types';
import { pageAnalyticsAttributes } from './utils';
import PageControls from './page-controls';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TooltipContainer = styled.span({
	display: 'inline-block',
	paddingLeft: token('space.100', '8px'),
	cursor: 'pointer',
	flexShrink: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledLink = styled(Link)({
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
	display: 'block',
});

const i18n = defineMessages({
	draftPageTooltip: {
		id: 'confluence-table-tree.draft.tooltip',
		defaultMessage: 'Drafts can only be seen by you.',
	},
	draftPageLozenge: {
		id: 'confluence-table-tree.draft.lozenge',
		defaultMessage: 'DRAFT',
	},
	defaultPageTitle: {
		id: 'confluence-table-tree.default.page.title',
		defaultMessage: 'Untitled',
	},
	showAllPagesButtonLabel: {
		id: 'confluence-table-tree.default.show-all-pages-button.label',
		defaultMessage: 'Show all pages',
	},
	showMorePagesButtonLabel: {
		id: 'confluence-table-tree.default.show-all-pages-button.label',
		defaultMessage: 'Show more pages',
	},
});

const PageTreeBase = ({
	readOnly,
	cloudId = null,
	onEdit,
	onView,
	onAddChildPage,
}: PageTreeBaseProps) => {
	const {
		items,
		errorType,
		rootContentId,
		spaceKey,
		expandSubtree,
		collapseSubtree,
		canShowMorePages,
		showMorePages,
	} = useConfluencePageTreeContext();
	const { formatMessage } = useIntl();
	const [stickyRowContentId, setStickyRowContentId] = useState<string | null>(null);
	const [{ isDraftsShown }] = useDraftsToggle();
	const handleRowExpand = useCallback(
		async (rowData: PageTreeItem, analyticsEvent?: UIAnalyticsEvent) => {
			if (analyticsEvent) {
				fireUIAnalytics(analyticsEvent, 'confluencePageTree');
			}
			await expandSubtree(rowData.id);
		},
		[expandSubtree],
	);

	const handleRowCollapse = useCallback(
		(rowData: PageTreeItem, analyticsEvent?: UIAnalyticsEvent) => {
			collapseSubtree(rowData.id);
			if (analyticsEvent) {
				fireUIAnalytics(analyticsEvent, 'confluencePageTree');
			}
		},
		[collapseSubtree],
	);

	const handleClickOnPageLink = useCallback(
		(
			event: React.MouseEvent,
			pageProperties: PageTreeItemProperties,
			analyticsEvent: UIAnalyticsEvent,
			origin: OriginTracing,
		) => {
			fireUIAnalytics(analyticsEvent, 'confluencePageTreeLink', {
				...origin.toAnalyticsAttributes({ hasGeneratedId: true }),
				...pageAnalyticsAttributes(pageProperties),
			});
			if (!(event.ctrlKey || event.metaKey || event.shiftKey)) {
				if (typeof onView === 'function' && !pageProperties.isDraft) {
					event.preventDefault();
					onView(pageProperties);
				} else if (typeof onEdit === 'function') {
					event.preventDefault();
					onEdit(pageProperties);
				}
			}
		},
		[onView, onEdit],
	);

	const handleShareToggle = useCallback((pageId: string | null) => {
		setStickyRowContentId(pageId);
	}, []);

	const getTableRow = useCallback(
		({ error, children, ...page }: PageTreeItem): ReactElement => {
			if (error) {
				return <ErrorRow />;
			}
			const {
				id,
				childTypes,
				hasDraftChildren,
				title,
				history: { contributors, lastUpdated } = {},
				isExpanded,
			} = page;

			const origin = new OriginTracing({ product: 'jira' });
			const pageItem = getPageTreeItemProperties(page);
			pageItem.url = origin.addToUrl(pageItem.url);
			pageItem.editUrl = origin.addToUrl(pageItem.editUrl);

			return (
				<Row
					itemId={id}
					items={children}
					hasChildren={childTypes.page.value || !!(hasDraftChildren && isDraftsShown)}
					isExpanded={isExpanded}
					onExpand={handleRowExpand}
					onCollapse={handleRowCollapse}
				>
					{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
					<Cell width="55%" singleLine className="cell x-start">
						<StyledLink
							onClick={(event: React.MouseEvent, analyticsEvent: UIAnalyticsEvent) =>
								handleClickOnPageLink(event, pageItem, analyticsEvent, origin)
							}
							href={pageItem.url}
							target="_blank"
							rel="noopener noreferrer"
							testId="confluence-project-pages.page-tree-row.page-title"
						>
							{title ? title : formatMessage(i18n.defaultPageTitle)}
						</StyledLink>
						{pageItem.isDraft && (
							<Tooltip content={formatMessage(i18n.draftPageTooltip)} position="top">
								<TooltipContainer>
									<Lozenge appearance="inprogress">{formatMessage(i18n.draftPageLozenge)}</Lozenge>
								</TooltipContainer>
							</Tooltip>
						)}
					</Cell>
					{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
					<Cell width="10%" className="cell">
						<Contributors
							cloudId={cloudId}
							contributors={contributors}
							isAvatarTooltipDisabled
							testId="confluence-project-pages.page-tree-row.contributors"
						/>
					</Cell>
					{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
					<Cell width="35%" singleLine className="cell x-end">
						{stickyRowContentId !== id && (
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
							<span className="hoverHide">
								<LastUpdated lastUpdated={lastUpdated} />
							</span>
						)}
						<span
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
							className={stickyRowContentId === id ? undefined : 'hoverShow'}
						>
							<PageControls
								page={pageItem}
								onEditClick={onEdit}
								onShareToggle={handleShareToggle}
								onAddChildClick={onAddChildPage}
								stickyRowContentId={stickyRowContentId}
								readOnly={readOnly}
								origin={origin}
							/>
						</span>
					</Cell>
				</Row>
			);
		},
		[
			cloudId,
			formatMessage,
			handleClickOnPageLink,
			handleRowCollapse,
			handleRowExpand,
			handleShareToggle,
			isDraftsShown,
			onAddChildPage,
			onEdit,
			readOnly,
			stickyRowContentId,
		],
	);

	let tableBody;
	if (rootContentId === null && spaceKey === null) {
		tableBody = null;
	} else if (errorType) {
		if (spaceKey) {
			tableBody = <ErrorState type={errorType} spaceKey={spaceKey} readOnly={readOnly} />;
		} else {
			tableBody = <ErrorState type={errorType} readOnly={readOnly} />;
		}
	} else {
		tableBody = (
			<HoverWrapper>
				<Rows key={rootContentId!} items={items!} render={getTableRow} />
			</HoverWrapper>
		);
	}

	return (
		<TableTreeWrapper data-testid="confluence-project-pages.page-tree.container">
			<TableTree>{tableBody}</TableTree>
			{canShowMorePages && <ShowMorePagesButton showMorePages={showMorePages} />}
		</TableTreeWrapper>
	);
};

const ShowMorePagesButton = ({ showMorePages }: { showMorePages: () => Promise<void> }) => {
	const { formatMessage } = useIntl();
	const [isLoading, setIsLoading] = useState(false);

	const loadMore = async () => {
		setIsLoading(true);
		await showMorePages();
		setIsLoading(false);
	};

	return (
		<ButtonWrapper>
			<Button onClick={loadMore} isLoading={isLoading}>
				{formatMessage(i18n.showMorePagesButtonLabel)}
			</Button>
		</ButtonWrapper>
	);
};

export default PageTreeBase;
